import { TextInput } from "north.js";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

// import { useSiteContext } from "src/contexts/SiteContext";
import { TWText } from "src/shared/styles";
import { XMarkIcon } from "src/static/icons";

type TTagInputProps = React.ComponentPropsWithRef<"input"> & {
  multi?: boolean;
  max?: number;
  options: string[];
  userOptions?: string[];
  setUserOptions?: (selected: string[]) => void;
  adding?: boolean;
  setAdding?: (adding: boolean) => void;
  selectedOptions: string[];
  setSelectedOptions: (selected: string[]) => void;
  canAdd?: boolean;
  errors?: string[];
  isRequired?: boolean;
  didEdit?: boolean;
  /** Show an X on selected predefined options (custom tags already have one). */
  showRemoveOnSelected?: boolean;
  /**
   * Use an uncontrolled add field so typing doesn't re-render on every
   * keystroke (avoids cursor/focus glitches with north.js TextInput).
   */
  stableAddInput?: boolean;
};

type TTagProps = React.ComponentPropsWithRef<"div"> & {
  selected: boolean;
  error?: boolean;
};

const ADD_PLACEHOLDER = "ex. Gaming";
const ADD_INPUT_PADDING_X = 52;
const ADD_MIN_WIDTH_FALLBACK = 130;
const ADD_MAX_WIDTH = 280;

const measureAddInputWidth = (() => {
  let canvas: HTMLCanvasElement | null = null;
  return (text: string) => {
    if (typeof document === "undefined") return ADD_MIN_WIDTH_FALLBACK;
    if (!canvas) canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    if (!context) return ADD_MIN_WIDTH_FALLBACK;
    context.font = "16px satoshi, sans-serif";
    return Math.ceil(context.measureText(text).width) + ADD_INPUT_PADDING_X;
  };
})();

const ADD_MIN_WIDTH = Math.max(
  ADD_MIN_WIDTH_FALLBACK,
  measureAddInputWidth(ADD_PLACEHOLDER)
);

const TagInput: React.FC<TTagInputProps> = ({
  multi,
  max,
  options,
  userOptions,
  setUserOptions,
  adding,
  setAdding,
  selectedOptions,
  setSelectedOptions,
  canAdd,
  errors = [],
  isRequired = false,
  didEdit = false,
  showRemoveOnSelected = false,
  stableAddInput = false,
  ...rest
}) => {
  const [inputWidth, setInputWidth] = useState(110);
  const [isFirst, setIsFirst] = useState(true);

  const toggleOption = (e: any, option: string) => {
    let newSelectedOptions: string[];
    e.target.blur();
    if (selectedOptions.includes(option)) {
      newSelectedOptions = selectedOptions.filter((o) => o !== option);
      if (userOptions && userOptions.includes(option)) {
        if (setUserOptions)
          setUserOptions(userOptions.filter((o) => o !== option));
      }
    } else {
      newSelectedOptions = multi ? [...selectedOptions, option] : [option];
    }
    if (max && newSelectedOptions.length > max) {
      return;
    }
    setSelectedOptions(newSelectedOptions);
  };

  const handleSubmit = (e: any) => {
    let newSelectedOptions: string[];
    let newUserOptions: string[] = [];
    if (
      selectedOptions.includes(e.target.value) ||
      e.target.value.trim() === "" ||
      (max && selectedOptions.length > max)
    ) {
      return;
    } else {
      newUserOptions = [...(userOptions ?? []), e.target.value.trim()];
      newSelectedOptions = [...selectedOptions, e.target.value.trim()];
    }
    if (max && newSelectedOptions.length > max) {
      return;
    }
    setSelectedOptions(newSelectedOptions);
    if (setUserOptions && newUserOptions) setUserOptions(newUserOptions);
    if (setAdding) setAdding(false);
    setInputWidth(110);
  };

  const commitStableOption = (raw: string) => {
    const value = raw.trim();
    if (
      !value ||
      selectedOptions.includes(value) ||
      (max != null && selectedOptions.length >= max)
    ) {
      if (setAdding) setAdding(false);
      return;
    }
    const newSelectedOptions = [...selectedOptions, value];
    if (max != null && newSelectedOptions.length > max) {
      if (setAdding) setAdding(false);
      return;
    }
    setSelectedOptions(newSelectedOptions);
    if (setUserOptions) {
      setUserOptions([...(userOptions ?? []), value]);
    }
    if (setAdding) setAdding(false);
  };

  const hasError = () => isRequired && didEdit && errors.length;

  return (
    <div>
      <TagContainer {...rest} css={[TWText.body]}>
        {options.map((option: string) => {
          return (
            <Tag
              key={option}
              selected={selectedOptions.includes(option)}
              onClick={(e: any) => toggleOption(e, option)}
              disabled={
                !!(
                  !selectedOptions.includes(option) &&
                  max &&
                  selectedOptions.length >= max
                )
              }
              error={!!(option.length > 30)}
            >
              {option}
              {showRemoveOnSelected && selectedOptions.includes(option) && (
                <XIcon
                  cursor="pointer"
                  onClick={(e: any) => toggleOption(e, option)}
                />
              )}
            </Tag>
          );
        })}
        {userOptions &&
          userOptions.map((option: string) => {
            return (
              <Tag
                selected={selectedOptions.includes(option)}
                disabled={
                  !!(
                    !selectedOptions.includes(option) &&
                    max &&
                    selectedOptions.length >= max
                  )
                }
                key={option}
                onClick={(e: any) => toggleOption(e, option)}
              >
                {option}
                <XIcon
                  cursor="pointer"
                  onClick={(e: any) => toggleOption(e, option)}
                />
              </Tag>
            );
          })}
        {adding &&
          (stableAddInput ? (
            <StableAddField
              onSubmit={commitStableOption}
              onCancel={() => {
                if (setAdding) setAdding(false);
              }}
            />
          ) : (
            <TextInputWrapper>
              <TextInputTag
                id="newInput"
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus={true}
                onKeyDown={(e: any) => {
                  if (e.key === "Enter") handleSubmit(e);
                }}
                onChange={(e: any) => {
                  const font = "12pt satoshi";
                  const canvas = document.createElement("canvas");
                  const context = canvas.getContext("2d");
                  if (!context) return;
                  context.font = font;
                  const { width } = context.measureText(e.target.value);
                  setInputWidth(width + ADD_INPUT_PADDING_X);
                  setIsFirst(false);
                }}
                onBlur={(e: any) => handleSubmit(e)}
                width={inputWidth}
                placeholder={isFirst ? ADD_PLACEHOLDER : ""}
              />
            </TextInputWrapper>
          ))}
        {canAdd && (
          <Tag
            key="plus"
            selected={false}
            disabled={!canAdd}
            onClick={() => {
              if (setAdding) setAdding(true);
            }}
          >
            +
          </Tag>
        )}
      </TagContainer>
      {hasError() ? (
        <div>Please select at least one option.</div>
      ) : (
        <div tw="h-24 w-10" />
      )}
    </div>
  );
};

/**
 * Uncontrolled add field — value + width live outside React state so keystrokes
 * never re-render (avoids cursor jump when the field grows).
 */
const StableAddField: React.FC<{
  onSubmit: (value: string) => void;
  onCancel: () => void;
}> = ({ onSubmit, onCancel }) => {
  const valueRef = useRef("");
  const doneRef = useRef(false);
  const ignoreBlurRef = useRef(true);
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    const focusId = window.setTimeout(() => {
      if (cancelled) return;
      inputRef.current?.focus();
      window.setTimeout(() => {
        if (!cancelled) ignoreBlurRef.current = false;
      }, 0);
    }, 0);
    return () => {
      cancelled = true;
      window.clearTimeout(focusId);
    };
  }, []);

  const finish = (value: string) => {
    if (doneRef.current) return;
    doneRef.current = true;
    onSubmit(value);
  };

  const cancel = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    onCancel();
  };

  return (
    <StableInputWrapper ref={wrapperRef} $width={ADD_MIN_WIDTH}>
      <StableAddInput
        ref={inputRef}
        defaultValue=""
        placeholder={ADD_PLACEHOLDER}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            ignoreBlurRef.current = false;
            finish(valueRef.current);
          } else if (e.key === "Escape") {
            e.preventDefault();
            ignoreBlurRef.current = false;
            cancel();
          }
        }}
        onChange={(e) => {
          valueRef.current = e.target.value;
          if (wrapperRef.current) {
            const text = e.target.value || ADD_PLACEHOLDER;
            wrapperRef.current.style.width = `${Math.min(
              ADD_MAX_WIDTH,
              Math.max(ADD_MIN_WIDTH, measureAddInputWidth(text))
            )}px`;
          }
        }}
        onBlur={() => {
          if (ignoreBlurRef.current) return;
          finish(valueRef.current);
        }}
      />
    </StableInputWrapper>
  );
};

export default TagInput;

const TagContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`;

const Tag = styled.button<TTagProps>`
  display: flex;
  height: 42px;
  padding: 8px 16px;
  margin: 8px 8px 8px 0px;
  color: ${({ theme }) => theme.globalConstants.color.textBody};
  border-radius: 8px;
  border: ${({ error, theme, selected }) =>
    selected
      ? `2px solid ${theme.globalConstants.color.borderPrimary}`
      : error
      ? `2px solid ${theme.globalConstants.color.borderDanger}`
      : `2px solid ${theme.globalConstants.color.borderGray}`};
  align-items: center;

  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};

  &:disabled {
    opacity: 0.45;
    background: ${({ theme }) =>
      theme.globalConstants.color.backgroundPrimaryLight};
  }
`;

const XIcon = styled(XMarkIcon)`
  width: 10px;
  height: 10px;
  margin-left: 8px;
`;

const TextInputWrapper = styled.div`
  width: fit-content;
`;

const TextInputTag = styled(TextInput)<{ width: number }>`
  width: ${({ width }) => (width ? `${width}px` : "100px")};
  height: 42px;
  padding: 8px 16px;
  margin: 8px 8px 8px 0px;
  border-radius: 8px;

  &::placeholder {
    color: ${({ theme }) =>
      theme.globalConstants.color.textTertiary} !important;
  }
`;

const StableInputWrapper = styled.div<{ $width: number }>`
  width: ${({ $width }) => $width}px;
  flex-shrink: 0;
  margin: 8px 8px 8px 0;
`;

const StableAddInput = styled.input`
  box-sizing: border-box;
  width: 100%;
  height: 42px;
  padding: 8px 16px;
  border-radius: 8px;
  border: 2px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 16px;
  color: ${({ theme }) => theme.globalConstants.color.textBody};
  background: transparent;
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.globalConstants.color.borderPrimary};
  }

  &::placeholder {
    color: ${({ theme }) => theme.globalConstants.color.textTertiary};
  }
`;
